import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type Sport, type Team, type Venue } from '../api';
import { useAuthStore } from '../auth';
import { AppShell } from '../components/AppShell';

export function DashboardPage() {
  const me = useAuthStore((s) => s.me);
  const [venues, setVenues] = useState<Venue[] | null>(null);
  const [teams, setTeams] = useState<Team[] | null>(null);
  const [sports, setSports] = useState<Sport[] | null>(null);

  useEffect(() => {
    api.listVenues().then(setVenues).catch(() => setVenues([]));
    api.listTeams().then(setTeams).catch(() => setTeams([]));
    api.listSports().then(setSports).catch(() => setSports([]));
  }, []);

  const venueCount = venues?.length ?? 0;
  const teamCount = teams?.length ?? 0;
  const displayCount =
    venues?.reduce((acc, v) => acc + (v._count?.displays ?? 0), 0) ?? 0;

  return (
    <AppShell>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            Welcome, {me?.user.name?.split(' ')[0] ?? 'there'}.
          </h1>
          <p className="mt-1 text-muted">
            {me?.org.name} · {me?.org.planTier}
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <StatCard
            label="Venues"
            value={venueCount}
            hint={venueCount === 0 ? 'Add your first venue' : 'Manage venues'}
            to="/venues"
          />
          <StatCard
            label="Teams"
            value={teamCount}
            hint={teamCount === 0 ? 'Add a team' : 'Manage rosters'}
            to="/teams"
          />
          <StatCard
            label="Displays"
            value={displayCount}
            hint={
              displayCount === 0 ? 'Pair a screen' : 'Connected'
            }
          />
          <StatCard
            label="Sports"
            value={sports?.filter((s) => s.isPhase1).length ?? 0}
            hint="Phase 1: BB, FB, Soccer"
          />
        </div>

        <div className="card">
          <h2 className="text-lg font-semibold">Next steps</h2>
          <ol className="mt-4 space-y-3 text-sm text-muted">
            <li className="flex gap-3">
              <span className="font-mono text-brand-light">01</span>
              <Link to="/venues" className="hover:text-text">
                Add a venue (gym, field, complex)
              </Link>
              {venueCount > 0 && <span className="text-emerald-400">✓ Done</span>}
            </li>
            <li className="flex gap-3">
              <span className="font-mono text-brand-light">02</span>
              <Link to="/teams" className="hover:text-text">
                Add a team and build its roster
              </Link>
              {teamCount > 0 && <span className="text-emerald-400">✓ Done</span>}
            </li>
            <li className="flex gap-3 opacity-60">
              <span className="font-mono text-brand-light">03</span>
              Pair your first display with a 6-character code — coming soon
            </li>
            <li className="flex gap-3 opacity-60">
              <span className="font-mono text-brand-light">04</span>
              Pick a sport, schedule a game, start scoring — coming soon
            </li>
          </ol>
        </div>
      </div>
    </AppShell>
  );
}

function StatCard({
  label,
  value,
  hint,
  to,
}: {
  label: string;
  value: number;
  hint: string;
  to?: string;
}) {
  const inner = (
    <>
      <div className="text-xs uppercase tracking-wider text-muted">{label}</div>
      <div className="mt-2 text-3xl font-bold">{value}</div>
      <div className="mt-1 text-sm text-muted">{hint}</div>
    </>
  );
  if (to) {
    return (
      <Link
        to={to}
        className="card transition hover:border-brand/40 hover:bg-card/80"
      >
        {inner}
      </Link>
    );
  }
  return <div className="card">{inner}</div>;
}
